import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { BookA, Check, Languages, Plus, Sparkles, Trash2 } from "lucide-react";
import { languageName, type CategoryDto, type FieldTranslations, type TranslationStatus } from "@dishfolio/contracts";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Input, NativeSelect, Progress, Sheet, Textarea, cn, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { relativeTime } from "../lib/format.ts";
import { useContent, useInvalidateMenu, useMenu, useTranslations } from "../lib/queries.ts";
import { PageContainer } from "../components/Page.tsx";
import { notifyError, UpgradeCard } from "../components/Upgrade.tsx";

const STATUS_LABEL: Record<TranslationStatus, { label: string; variant: "secondary" | "warning" | "success" | "destructive" | "outline" }> = {
  missing: { label: "Missing", variant: "outline" },
  draft: { label: "Draft", variant: "secondary" },
  needs_review: { label: "Needs review", variant: "warning" },
  approved: { label: "Approved", variant: "success" },
  stale: { label: "Outdated", variant: "destructive" },
};

type Entry = { kind: "category" | "item" | "variant" | "modifier_group" | "modifier_option"; id: string; name?: string | null; description?: string | null; status: "draft" | "approved" | "needs_review" };

function Row({ label, source, sourceDescription, tr, onChange, kind }: { label: string; source: string; sourceDescription?: string | null; tr?: FieldTranslations | { name: string | null; description?: null; status: TranslationStatus; nameStatus?: TranslationStatus }; onChange: (e: Partial<Entry>) => void; kind: Entry["kind"] }) {
  const [name, setName] = useState(tr?.name ?? "");
  const [desc, setDesc] = useState(tr?.description ?? "");
  useEffect(() => {
    setName(tr?.name ?? "");
    setDesc(tr?.description ?? "");
  }, [tr?.name, tr?.description]);
  const status = tr?.status ?? "missing";
  return (
    <div className="grid gap-3 border-b py-3 last:border-0 md:grid-cols-2">
      <div className="min-w-0">
        <div className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{label}</div>
        <div className="font-medium">{source}</div>
        {sourceDescription ? <div className="text-sm text-muted-foreground">{sourceDescription}</div> : null}
      </div>
      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <Input value={name} placeholder={source} onChange={(e) => (setName(e.target.value), onChange({ name: e.target.value || null }))} className="h-8" />
          <Badge variant={STATUS_LABEL[status].variant}>{STATUS_LABEL[status].label}</Badge>
        </div>
        {sourceDescription && (kind === "item" || kind === "category") ? (
          <Textarea rows={2} value={desc} placeholder={sourceDescription} onChange={(e) => (setDesc(e.target.value), onChange({ description: e.target.value || null }))} className="min-h-14 text-sm" />
        ) : null}
        {status !== "approved" && status !== "missing" && tr?.name ? (
          <Button variant="ghost" size="sm" className="justify-self-start" onClick={() => onChange({ name, description: desc || null, status: "approved" })}>
            <Check /> Approve
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function AiDialog({ menuId, languages, open, onOpenChange }: { menuId: string; languages: string[]; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>(languages);
  const [scope, setScope] = useState<"changed" | "all_unapproved">("changed");
  const [loading, setLoading] = useState(false);
  useEffect(() => setSelected(languages), [languages]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Translate with AI</DialogTitle>
          <DialogDescription>Only missing and outdated fields are sent. Each translated item costs 1 credit. AI never adds ingredients, allergens or dietary claims — anything mentioning them is marked for your review.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          {languages.map((l) => (
            <label key={l} className="flex items-center gap-2 text-sm">
              <Checkbox checked={selected.includes(l)} onCheckedChange={(v) => setSelected(v ? [...selected, l] : selected.filter((x) => x !== l))} /> {languageName(l)}
            </label>
          ))}
        </div>
        <NativeSelect value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
          <option value="changed">Only missing and outdated fields</option>
          <option value="all_unapproved">Re-translate everything not approved by staff</option>
        </NativeSelect>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!selected.length}
            onClick={async () => {
              setLoading(true);
              try {
                const res = await api.post<{ job: { fieldCount: number; creditsCharged: number } }>(`/menus/${menuId}/translations`, { languages: selected, scope });
                toast.success(res.job.fieldCount ? `Translating ${res.job.fieldCount} fields (${res.job.creditsCharged} credits)` : "Everything is already translated");
                await qc.invalidateQueries({ queryKey: ["translations", menuId] });
                await qc.invalidateQueries({ queryKey: ["org"] });
                onOpenChange(false);
              } catch (err) {
                notifyError(err);
              } finally {
                setLoading(false);
              }
            }}
          >
            <Sparkles /> Translate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GlossarySheet({ restaurantId, languages, open, onOpenChange }: { restaurantId: string; languages: string[]; open: boolean; onOpenChange: (o: boolean) => void }) {
  const glossary = useQuery({ queryKey: ["glossary", restaurantId], queryFn: () => api.get<{ entries: Array<{ source: string; language: string; target: string; keep: boolean }> }>(`/restaurants/${restaurantId}/glossary`), enabled: open });
  const [rows, setRows] = useState<Array<{ source: string; language: string; target: string; keep: boolean }>>([]);
  useEffect(() => setRows(glossary.data?.entries ?? []), [glossary.data]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Glossary">
      <p className="text-sm text-muted-foreground">Fix how specific terms are translated, or keep dish names untranslated (e.g. “Ćevapi”, “Burek”).</p>
      <div className="grid gap-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_80px_1fr_auto] items-center gap-2">
            <Input value={r.source} placeholder="Term" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, source: e.target.value } : x)))} />
            <NativeSelect value={r.language} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, language: e.target.value } : x)))}>
              {languages.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </NativeSelect>
            <Input value={r.keep ? r.source : r.target} disabled={r.keep} placeholder="Translation" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)))} />
            <Button variant="ghost" size="icon-sm" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove">
              <Trash2 />
            </Button>
            <label className="col-span-4 -mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox checked={r.keep} onCheckedChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, keep: Boolean(v), target: v ? x.source : x.target } : x)))} /> Keep the original term
            </label>
          </div>
        ))}
        <Button variant="outline" size="sm" className="justify-self-start" onClick={() => setRows([...rows, { source: "", language: languages[0] ?? "en", target: "", keep: false }])}>
          <Plus /> Add term
        </Button>
      </div>
      <Button
        onClick={async () => {
          try {
            await api.put(`/restaurants/${restaurantId}/glossary`, { entries: rows.filter((r) => r.source.trim()).map((r) => ({ ...r, target: r.keep ? r.source : r.target || r.source })) });
            toast.success("Glossary saved");
            onOpenChange(false);
          } catch (err) {
            notifyError(err);
          }
        }}
      >
        Save glossary
      </Button>
    </Sheet>
  );
}

export function MenuTranslationsPage() {
  const { menuId } = useParams({ from: "/app/menus/$menuId/translations" });
  const { org, can } = useOrg();
  const menu = useMenu(menuId);
  const overview = useTranslations(menuId);
  const content = useContent(menuId);
  const invalidate = useInvalidateMenu(menuId);
  const [language, setLanguage] = useState<string | null>(null);
  const [pending, setPending] = useState<Record<string, Entry>>({});
  const [aiOpen, setAiOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const m = menu.data?.menu;
  const targets = m ? m.enabledLanguages.filter((l) => l !== m.defaultLanguage) : [];
  const lang = language ?? targets[0] ?? null;
  const running = overview.data?.jobs.some((j) => j.status === "queued" || j.status === "running");
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      void overview.refetch();
      void invalidate();
    }, 2000);
    return () => clearInterval(t);
  }, [running]);

  const edit = (kind: Entry["kind"], id: string, patch: Partial<Entry>) =>
    setPending((p) => ({ ...p, [`${kind}:${id}`]: { ...(p[`${kind}:${id}`] ?? { kind, id, status: "approved" }), ...patch } }));

  const save = async () => {
    if (!lang) return;
    setSaving(true);
    try {
      await api.put(`/menus/${menuId}/translations/manual`, { language: lang, entries: Object.values(pending) });
      setPending({});
      await invalidate();
      toast.success("Translations saved");
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => content.data ?? ([] as CategoryDto[]), [content.data]);
  if (!m) return null;
  if (targets.length === 0) {
    return (
      <PageContainer wide>
        <EmptyState
          icon={<Languages />}
          title="Add a second language"
          description={`Your menu is in ${languageName(m.defaultLanguage)}. Add languages for tourists in the menu settings — guests pick their language on the menu.`}
          action={
            <Button asChild>
              <Link to="/menus/$menuId/settings" params={{ menuId }}>
                Add languages
              </Link>
            </Button>
          }
        />
      </PageContainer>
    );
  }
  const counts = overview.data?.languages.find((l) => l.language === lang)?.counts;
  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;
  return (
    <PageContainer wide>
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <div className="grid content-start gap-4">
          <Card className="gap-3">
            <CardTitle>Languages</CardTitle>
            {overview.data?.languages.map((l) => {
              const t = Object.values(l.counts).reduce((a, b) => a + b, 0);
              const done = l.counts.approved + l.counts.draft;
              return (
                <button key={l.language} type="button" onClick={() => setLanguage(l.language)} className={cn("grid cursor-pointer gap-1.5 rounded-lg border p-2.5 text-left", l.language === lang && "border-primary ring-2 ring-primary/15")}>
                  <div className="flex justify-between text-sm font-medium">
                    {languageName(l.language)}
                    <span className="text-muted-foreground">{t ? Math.round((done / t) * 100) : 100}%</span>
                  </div>
                  <Progress value={t ? (done / t) * 100 : 100} className="h-1.5" />
                  {l.counts.stale + l.counts.needs_review > 0 ? (
                    <div className="text-xs text-destructive">
                      {l.counts.stale ? `${l.counts.stale} outdated ` : ""}
                      {l.counts.needs_review ? `${l.counts.needs_review} need review` : ""}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </Card>
          {org?.plan.aiTranslation ? (
            <Button onClick={() => setAiOpen(true)} disabled={running || !can("translation:edit")}>
              <Sparkles /> {running ? "Translating…" : "Translate with AI"}
            </Button>
          ) : (
            <UpgradeCard title="AI translation" description="Translate your whole menu into any language in seconds, then review. Pro includes 500 credits a month." />
          )}
          <Button variant="outline" onClick={() => setGlossaryOpen(true)}>
            <BookA /> Glossary
          </Button>
          {overview.data?.jobs.length ? (
            <Card className="gap-2">
              <CardDescription>Recent AI jobs</CardDescription>
              {overview.data.jobs.slice(0, 5).map((j) => (
                <div key={j.id} className="flex items-center justify-between text-xs">
                  <span>
                    {j.languages.map((l) => l.toUpperCase()).join(", ")} · {j.fieldCount} fields
                  </span>
                  <Badge variant={j.status === "completed" ? "success" : j.status === "failed" ? "destructive" : "secondary"}>{j.status}</Badge>
                </div>
              ))}
              <div className="text-[11px] text-muted-foreground">Last: {relativeTime(overview.data.jobs[0]!.createdAt)}</div>
            </Card>
          ) : null}
        </div>
        <Card>
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>
                {languageName(m.defaultLanguage)} → {lang ? languageName(lang) : ""}
              </CardTitle>
              <CardDescription>
                Guests see the original text wherever a translation is missing, outdated or awaiting review.{counts && total ? ` ${counts.missing + counts.stale} of ${total} need attention.` : ""}
              </CardDescription>
            </div>
            <Button onClick={save} loading={saving} disabled={!Object.keys(pending).length || !can("translation:edit")}>
              Save {Object.keys(pending).length ? `(${Object.keys(pending).length})` : ""}
            </Button>
          </CardHeader>
          <div>
            {lang
              ? rows.map((c) => (
                  <div key={c.id} className="mb-4">
                    <Row label="Category" kind="category" source={c.name} sourceDescription={c.description} tr={c.translations[lang]} onChange={(p) => edit("category", c.id, p)} />
                    {c.items.map((i) => (
                      <div key={i.id} className="pl-4">
                        <Row label="Item" kind="item" source={i.name} sourceDescription={i.description} tr={i.translations[lang]} onChange={(p) => edit("item", i.id, p)} />
                        {i.variants.map((v) => (
                          <div key={v.id} className="pl-4">
                            <Row label="Size" kind="variant" source={v.name} tr={v.translations[lang] ? { name: v.translations[lang]!, status: "approved" } : undefined} onChange={(p) => edit("variant", v.id, p)} />
                          </div>
                        ))}
                        {i.modifierGroups.map((g) => (
                          <div key={g.id} className="pl-4">
                            <Row label="Choice group" kind="modifier_group" source={g.name} tr={g.translations[lang] ? { name: g.translations[lang]!, status: "approved" } : undefined} onChange={(p) => edit("modifier_group", g.id, p)} />
                            {g.options.map((o) => (
                              <div key={o.id} className="pl-4">
                                <Row label="Option" kind="modifier_option" source={o.name} tr={o.translations[lang] ? { name: o.translations[lang]!, status: "approved" } : undefined} onChange={(p) => edit("modifier_option", o.id, p)} />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))
              : null}
            <p className="mt-2 text-xs text-muted-foreground">Headings and other text in your design are translated in the Design editor (switch the editing language in the toolbar), or automatically by AI translation.</p>
          </div>
        </Card>
      </div>
      {lang ? <AiDialog menuId={menuId} languages={targets} open={aiOpen} onOpenChange={setAiOpen} /> : null}
      <GlossarySheet restaurantId={m.restaurantId} languages={targets} open={glossaryOpen} onOpenChange={setGlossaryOpen} />
    </PageContainer>
  );
}
